import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { PopularLabelData, StyleType } from '../popularLabelsTypes.ts';
import { toStyleEnum } from '../utils/popularLabelsParamsUtils.ts';

import type {
	usePopularLabelsDataQuery$data,
	usePopularLabelsDataQuery$variables,
} from './__generated__/usePopularLabelsDataQuery.graphql.ts';
import type {
	usePopularLabelsDataSpaceIdQuery$data,
	usePopularLabelsDataSpaceIdQuery$variables,
} from './__generated__/usePopularLabelsDataSpaceIdQuery.graphql.ts';

interface UsePopularLabelsQueryOptions {
	spaceId: string | null;
	first: number;
	style: StyleType;
	/**
	 * When true, the underlying GraphQL query is not issued. Used by
	 * `usePopularLabelsDataForSpace` to avoid firing the labels query until a
	 * real `spaceId` has been resolved.
	 */
	skip?: boolean;
}

interface UsePopularLabelsDataResult {
	data: PopularLabelData[] | undefined;
	loading: boolean;
	error: Error | null;
}

const mapResponseToLabels = (
	data: usePopularLabelsDataQuery$data | undefined,
): PopularLabelData[] | undefined => {
	if (!data) {
		return undefined;
	}

	const connection = data.confluence_popularLabelsMacro;
	if (connection == null) {
		return undefined;
	}

	const nodes = connection.nodes;
	if (!nodes) {
		return [];
	}

	const labels: PopularLabelData[] = [];

	for (const node of nodes) {
		if (node == null || node.labelId == null || node.name == null) {
			continue;
		}

		labels.push({
			name: node.name,
			rank: node.rank ?? 0,
			labelId: node.labelId,
		});
	}

	return labels;
};

/**
 * Core query hook. Always issues `usePopularLabelsDataQuery` with the given
 * `spaceId` (`null` for global). This hook does not resolve `spaceKey` itself
 * — callers should resolve to a `spaceId` first (or pass `null` for global).
 */
export const usePopularLabelsData = ({
	spaceId,
	first,
	style,
	skip,
}: UsePopularLabelsQueryOptions): UsePopularLabelsDataResult => {
	const { cloudId } = useSessionData();

	const variables: usePopularLabelsDataQuery$variables = {
		cloudId,
		first,
		style: toStyleEnum(style),
		spaceId,
	};

	const { data, loading, error } = useQueryInterop<
		usePopularLabelsDataQuery$data,
		usePopularLabelsDataQuery$variables
	>(
		graphql`
			query usePopularLabelsDataQuery(
				$cloudId: ID!
				$first: Int
				$spaceId: ID
				$style: ConfluencePopularLabelStyle
			) {
				confluence_popularLabelsMacro(
					cloudId: $cloudId
					first: $first
					spaceId: $spaceId
					style: $style
				) {
					nodes {
						name
						rank
						labelId
					}
				}
			}
		`,
		// Preserve GraphQL errors returned with partial data so the renderer's
		// existing error state wins over the empty-state branch.
		{ variables, skip: !!skip, errorPolicy: 'all', fetchPolicy: 'cache-first' },
	);
	const isAwaitingInitialResult = !skip && data === undefined && !error;

	return {
		data: mapResponseToLabels(data),
		loading: loading || isAwaitingInitialResult,
		error: error ?? null,
	};
};

/**
 * Variant for the space-scoped case. Resolves `spaceKey` → `spaceId` via a
 * dedicated GraphQL query (mirrors `space-utils`' `SpaceIdentifiersQuery`
 * shape — `space(key: $spaceKey) { id }`). We borrow the schema rather than
 * going through `useSpaceId` so we can observe the resolution's `loading`
 * and `error` state and distinguish "still resolving" from "no such space".
 *
 * Behavior parity with the legacy backend macro:
 *   - Loading            → spinner
 *   - Error              → error display
 *   - Resolved, no space → empty-state ("No labels match these criteria.")
 *   - Resolved, found    → fire popular-labels query for that spaceId
 */
export const usePopularLabelsDataForSpace = ({
	spaceKey,
	first,
	style,
}: {
	spaceKey: string;
	first: number;
	style: StyleType;
}): UsePopularLabelsDataResult => {
	const {
		data: spaceData,
		loading: spaceLoading,
		error: spaceError,
	} = useQueryInterop<
		usePopularLabelsDataSpaceIdQuery$data,
		usePopularLabelsDataSpaceIdQuery$variables
	>(
		graphql`
			query usePopularLabelsDataSpaceIdQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
				}
			}
		`,
		{ variables: { spaceKey }, fetchPolicy: 'cache-first' },
	);

	const resolvedSpaceId = spaceData?.space?.id ?? null;
	const spaceIsAwaitingInitialResult = spaceData === undefined && !spaceLoading && !spaceError;
	const spaceUnresolved =
		!spaceIsAwaitingInitialResult && !spaceLoading && !spaceError && !resolvedSpaceId;

	// Skip the labels query whenever we don't have a real spaceId — this
	// covers all three "not ready" cases: still loading, errored, and
	// settled-with-no-match.
	const result = usePopularLabelsData({
		spaceId: resolvedSpaceId,
		first,
		style,
		skip: !resolvedSpaceId,
	});

	return {
		// When the space lookup settled with no match, surface an empty list so
		// the renderer falls into its empty-state branch — matching the legacy
		// backend macro's behavior for unresolvable `spaceKey` values.
		data: spaceUnresolved ? [] : result.data,
		loading: spaceLoading || spaceIsAwaitingInitialResult || result.loading,
		error: spaceError ?? result.error,
	};
};
